import { useEffect, useState, useCallback, useMemo } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'

const tocItems = [
  { id: '1-system-overview', label: 'System Overview' },
  { id: '2-login-and-account', label: 'Login and Account' },
  { id: '3-interface-layout', label: 'Interface Layout' },
  { id: '4-objects-and-data-browsing', label: 'Objects and Data Browsing' },
  { id: '5-data-list-page-owl', label: 'Data List Page (OWL)' },
  { id: '6-record-detail-page-ti', label: 'Record Detail Page (TI)' },
  { id: '7-business-conversion-flow', label: 'Business Conversion Flow' },
  { id: '8-attachment-management', label: 'Attachment Management' },
  { id: '9-notes', label: 'Notes' },
  { id: '10-change-history', label: 'Change History' },
  { id: '11-custom-actions-qr-code-and-share', label: 'Custom Actions (QR Code & Share)' },
  { id: '12-mashup-embedded-pages', label: 'Mashup Embedded Pages' },
  { id: '13-admin-panel-object-management', label: 'Admin Panel \u2014 Object Management' },
  { id: '14-admin-panel-key-user-management', label: 'Admin Panel \u2014 Key User Management' },
  { id: '15-admin-panel-role-and-permission-management', label: 'Admin Panel \u2014 Role & Permission Management' },
  { id: '16-admin-panel-api-token-management', label: 'Admin Panel \u2014 API Token Management' },
  { id: '17-admin-panel-mashup-management', label: 'Admin Panel \u2014 Mashup Management' },
  { id: '18-admin-panel-custom-action-management', label: 'Admin Panel \u2014 Custom Action Management' },
  { id: '19-admin-panel-data-import', label: 'Admin Panel \u2014 Data Import' },
  { id: '20-admin-panel-data-workbench-and-deleted-data', label: 'Admin Panel \u2014 Data Workbench & Deleted Data' },
  { id: '21-admin-panel-login-logs-and-action-logs', label: 'Admin Panel \u2014 Login Logs & Action Logs' },
  { id: '22-admin-panel-attachment-management', label: 'Admin Panel \u2014 Attachment Management' },
  { id: '23-permission-system-explained', label: 'Permission System Explained' },
  { id: '24-preset-objects-and-fields-reference', label: 'Preset Objects & Fields Reference' },
  { id: '25-field-type-reference', label: 'Field Type Reference' },
  { id: '26-faq', label: 'FAQ' },
]

export function HelpPage() {
  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetch('/docs/help/index.html')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load help document')
        return res.text()
      })
      .then((text) => {
        setContent(text)
        setLoading(false)
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load')
        setLoading(false)
      })
  }, [])

  const handleLinkClick = useCallback((e: React.MouseEvent, id: string) => {
    e.preventDefault()
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [])

  const handleContentClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    if (target.tagName === 'A') {
      const href = target.getAttribute('href')
      if (href && href.startsWith('#')) {
        e.preventDefault()
        const id = href.slice(1)
        const el = document.getElementById(id)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }
    }
  }, [])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    )
  }

  const sidebarWidth = 260

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      {/* Sidebar */}
      <Box
        sx={{
          width: sidebarWidth,
          flexShrink: 0,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Contents
          </Typography>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
          <ol style={{ listStyleType: 'decimal', paddingLeft: '1.25rem', margin: 0 }}>
            {tocItems.map((item, index) => (
              <li
                key={item.id}
                style={{
                  margin: '0.2rem 0',
                }}
              >
                <a
                  href={`#${item.id}`}
                  onClick={(e) => handleLinkClick(e, item.id)}
                  style={{
                    color: 'inherit',
                    textDecoration: 'none',
                    display: 'block',
                    padding: '0.25rem 0.35rem',
                    borderRadius: '0.375rem',
                    transition: 'background-color 0.2s',
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.04)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ol>
        </Box>
      </Box>

      {/* Content */}
      <Box
        onClick={handleContentClick}
        sx={{
          flex: 1,
          height: '100%',
          overflow: 'auto',
          p: 3,
        }}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    </Box>
  )
}
